<script setup>
  import {inject, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {usePage} from "@inertiajs/vue3";
  import MergeAccountIconGroup from "@pages/MergeAccount/Components/IconGroup.vue";
  import {MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  import Crypt from "@/resources/js/crypt.js";

  defineOptions({name: "MergeAccountShowStepsProcessing"});

  defineProps({
    processingPercentage: {
      type: Number,
      required: true,
    },
  });

  const emit = defineEmits(["ready"]);

  const privacy = inject("$privacy");
  const pageProperties = usePage().props;

  const sourcePlaintext = ref(null);
  const targetPlaintext = ref(null);

  const sourceKey = ref(null);
  const targetKey = ref(null);

  const sourceValidation = ref(null);
  const targetValidation = ref(null);

  onMounted(() => {
    // which key do we already know
    // from the logged-in user?
    // source...
    if (privacy.getClient()?.id === pageProperties.source.id) {
      sourceKey.value = privacy.getRecoveryCode().split(" ").join("");
    }
    // ...or target?
    if (privacy.getClient()?.id === pageProperties.target.id) {
      targetKey.value = privacy.getRecoveryCode().split(" ").join("");
    }
  });

  watch(
    () => sourcePlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.source.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            sourceKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error decrypting source user encryption", error);
            sourceValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch(
    () => targetPlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.target.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            targetKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error decrypting target user encryption", error);
            targetValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch([() => sourceKey.value, () => targetKey.value], ([newSourceKey, newTargetKey]) => {
    if (newSourceKey !== null && newTargetKey !== null) {
      emit("ready", {sourceKey: newSourceKey, targetKey: newTargetKey});
    }
  });
</script>

<template>
  <div>
    <MergeAccountIconGroup>
      <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
      <ComponentIcon v-else class="shrink-0" custom="front-desk" />

      <div class="px-4">
        <MedSpinner size="xl" :data-test="getTestId('steps-processing-spinner')" />
      </div>

      <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
      <ComponentIcon v-else class="shrink-0" custom="front-desk" />
    </MergeAccountIconGroup>

    <h3 class="mt-4 text-xl font-semibold">Wir übertragen Ihre Patienten und Fälle</h3>

    <div class="mt-4 text-center" />

    <div class="relative mt-4 h-4 w-full rounded-md bg-red-100 shadow-md">
      <div class="absolute top-0 left-0 h-4 w-8 rounded-md bg-mcred" :style="'width:' + processingPercentage + '%'" />
    </div>

    <ComponentAlert class="mt-8">Bitte unterbrechen Sie diesen Vorgang nicht!</ComponentAlert>
  </div>
</template>
